import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Check, Building2, User, Globe, AlertTriangle, Target, Sparkles, SkipForward, Edit2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INDUSTRY_OPTIONS = [
  'Importer or Distributor', 'Manufacturer', 'Government Procurement',
  'Defence or Aerospace', 'Retail', 'Food and Agriculture', 'Other',
];
const SIZE_OPTIONS = ['1 to 10', '11 to 50', '51 to 200', '201 to 1000', '1000+'];
const RESPONSIBILITY_OPTIONS = [
  'Strategic sourcing', 'Procurement operations', 'Supply chain risk',
  'Compliance', 'Executive or leadership', 'Other',
];
const IMPORT_VALUES = ['Under $1m', '$1m to $10m', '$10m to $100m', '$100m+', 'Prefer not to say'];
const PRODUCT_CATEGORIES = [
  'Electronics', 'Machinery', 'Textiles or Apparel', 'Food and Beverage',
  'Chemicals', 'Metals and Raw Materials', 'Medical and Pharmaceutical',
  'Defence and Aerospace', 'Other',
];
const RISK_OPTIONS = [
  'Geopolitical', 'Trade and Tariff', 'Economic and Currency',
  'Sanctions and Compliance', 'Logistics and Shipping', 'Natural Disasters',
  'Labour and ESG', 'Supplier Financial Health',
];
const GOAL_OPTIONS = [
  'Diversify suppliers', 'Reduce country concentration risk',
  'Meet compliance or sovereign sourcing requirements', 'Faster reaction to disruptions',
  'Better executive reporting', 'Evaluate new markets', 'Other',
];
const ALERT_FREQ = ['Real time', 'Daily digest', 'Weekly digest'];
const REPORT_CADENCE = ['Weekly', 'Monthly', 'Quarterly', 'On demand only'];

const COUNTRIES = [
  'Australia','Bangladesh','Brazil','Cambodia','Canada','Chile','China','Colombia','Egypt',
  'France','Germany','India','Indonesia','Israel','Italy','Japan','Kenya','Laos','Malaysia',
  'Mexico','Morocco','Myanmar','New Zealand','Nigeria','Pakistan','Papua New Guinea',
  'Peru','Philippines','Poland','Saudi Arabia','Singapore','South Africa','South Korea',
  'Spain','Sweden','Taiwan','Thailand','Turkiye','United Arab Emirates','United Kingdom',
  'United States','Vietnam',
];

// Countries with monitored ports — eligible as home country (destination of freight routes)
const HOME_COUNTRIES = [
  { code: 'AU', name: 'Australia' }, { code: 'NZ', name: 'New Zealand' }, { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'DE', name: 'Germany' }, { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' }, { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' }, { code: 'GR', name: 'Greece' },
  { code: 'SG', name: 'Singapore' }, { code: 'MY', name: 'Malaysia' }, { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' }, { code: 'ID', name: 'Indonesia' }, { code: 'PH', name: 'Philippines' },
  { code: 'CN', name: 'China' }, { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'TW', name: 'Taiwan' },
  { code: 'IN', name: 'India' }, { code: 'LK', name: 'Sri Lanka' }, { code: 'BD', name: 'Bangladesh' },
  { code: 'AE', name: 'United Arab Emirates' }, { code: 'SA', name: 'Saudi Arabia' },
  { code: 'MX', name: 'Mexico' }, { code: 'BR', name: 'Brazil' }, { code: 'PG', name: 'Papua New Guinea' },
];

export default function NewOnboardingPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    organisation_name: '',
    industry_sector: '',
    organisation_size: '',
    countries_of_operation: ['Australia'],
    home_country: 'AU',
    full_name: '',
    job_title: '',
    primary_responsibility: '',
    source_countries: [],
    annual_import_value: '',
    product_categories: [],
    risk_priorities: [],
    current_monitoring: '',
    goals: [],
    goals_other: '',
    alert_frequency: 'Daily digest',
    report_cadence: 'Monthly',
  });

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        organisation_name: f.organisation_name || user.name || '',
        full_name: f.full_name || user.name || '',
      }));
    }
  }, [user]);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleMulti = (key, val) => {
    setForm(f => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/onboarding/skip`);
      await refreshUser();
      navigate('/dashboard');
    } catch { navigate('/dashboard'); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/profile`, form);
      await refreshUser();
      navigate('/dashboard');
    } catch { /* still navigate */ navigate('/dashboard'); }
    setSaving(false);
  };

  const totalSteps = 6;
  const progressPct = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-[#0A2533]" data-testid="onboarding-wizard">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1.5 bg-[#0A2533]">
        <div className="h-full bg-[#00707C] transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="onboarding-progress" />
      </div>
      <div className="fixed top-2 left-0 right-0 z-50 flex justify-center gap-2 py-2">
        {[1,2,3,4,5,6].map(s => (
          <div key={s} className={`w-2 h-2 rounded-full transition-colors ${s <= step ? 'bg-[#00707C]' : 'bg-white/20'}`} />
        ))}
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-16 pb-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 min-h-[480px] flex flex-col">

          {/* Step 1 */}
          {step === 1 && (
            <StepContainer icon={Building2} title="About your organisation" subtitle="Tell us about your business so we can tailor your experience.">
              <Field label="Organisation name" value={form.organisation_name} onChange={v => update('organisation_name', v)} testId="onb-org-name" />
              <SelectField label="Industry sector" value={form.industry_sector} options={INDUSTRY_OPTIONS} onChange={v => update('industry_sector', v)} testId="onb-industry" />
              <SelectField label="Organisation size" value={form.organisation_size} options={SIZE_OPTIONS} onChange={v => update('organisation_size', v)} testId="onb-size" />
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5 mt-4">Country of operation</label>
              <MultiChip items={['Australia', 'New Zealand', 'United Kingdom', 'United States', 'Singapore']} selected={form.countries_of_operation} toggle={v => toggleMulti('countries_of_operation', v)} testId="onb-country-op" />
              <div className="mb-3 mt-4">
                <label htmlFor="home-country-select" className="block text-sm font-medium text-[#4E6974] mb-1.5">Home country</label>
                <select id="home-country-select" value={form.home_country} onChange={e => update('home_country', e.target.value)} data-testid="home-country-select"
                  className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#0A2533] focus:border-[#0097A7] outline-none bg-white">
                  {HOME_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
                <p className="text-xs text-[#4E6974] mt-1.5">Freight routes and port intelligence will be calculated to your home country's ports.</p>
              </div>
            </StepContainer>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <StepContainer icon={User} title="Your role" subtitle="Help us understand how you will use TradePath.">
              <Field label="Full name" value={form.full_name} onChange={v => update('full_name', v)} testId="onb-name" />
              <Field label="Job title" value={form.job_title} onChange={v => update('job_title', v)} testId="onb-title" />
              <SelectField label="Primary responsibility" value={form.primary_responsibility} options={RESPONSIBILITY_OPTIONS} onChange={v => update('primary_responsibility', v)} testId="onb-responsibility" />
            </StepContainer>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <StepContainer icon={Globe} title="Supply chain footprint" subtitle="Which countries and products are part of your supply chain?">
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5">Countries you source from or are considering</label>
              <div className="max-h-40 overflow-y-auto border border-[#E0E0E0] rounded-lg p-2 mb-4">
                <MultiChip items={COUNTRIES} selected={form.source_countries} toggle={v => toggleMulti('source_countries', v)} testId="onb-source" small />
              </div>
              <SelectField label="Approximate annual import or procurement value" value={form.annual_import_value} options={IMPORT_VALUES} onChange={v => update('annual_import_value', v)} testId="onb-import-val" />
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5 mt-4">Primary product or commodity categories</label>
              <MultiChip items={PRODUCT_CATEGORIES} selected={form.product_categories} toggle={v => toggleMulti('product_categories', v)} testId="onb-products" />
            </StepContainer>
          )}

          {/* Step 4 */}
          {step === 4 && (
            <StepContainer icon={AlertTriangle} title="Risk priorities" subtitle="Select the risks that matter most to your organisation (top 3).">
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5">Which risks matter most?</label>
              <MultiChip items={RISK_OPTIONS} selected={form.risk_priorities} toggle={v => toggleMulti('risk_priorities', v)} testId="onb-risks" />
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5 mt-4">How do you currently monitor these risks? (optional)</label>
              <textarea value={form.current_monitoring} onChange={e => update('current_monitoring', e.target.value)}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm min-h-[80px] focus:border-[#0097A7] outline-none" placeholder="e.g. Manual spreadsheets, news monitoring, consultant reports..."
                data-testid="onb-monitoring" />
            </StepContainer>
          )}

          {/* Step 5 */}
          {step === 5 && (
            <StepContainer icon={Target} title="Goals with TradePath" subtitle="What are you hoping to achieve?">
              <MultiChip items={GOAL_OPTIONS} selected={form.goals} toggle={v => toggleMulti('goals', v)} testId="onb-goals" />
              {form.goals.includes('Other') && (
                <Field label="Tell us more" value={form.goals_other} onChange={v => update('goals_other', v)} testId="onb-goals-other" />
              )}
              <SelectField label="Preferred alert frequency" value={form.alert_frequency} options={ALERT_FREQ} onChange={v => update('alert_frequency', v)} testId="onb-alert-freq" />
              <SelectField label="Preferred report cadence" value={form.report_cadence} options={REPORT_CADENCE} onChange={v => update('report_cadence', v)} testId="onb-report-cadence" />
            </StepContainer>
          )}

          {/* Step 6: Summary */}
          {step === 6 && (
            <StepContainer icon={Sparkles} title="Review and confirm" subtitle="Here is a summary of your responses. Click Edit to change any section.">
              <div className="space-y-3 text-sm">
                <SummaryRow label="Organisation" value={`${form.organisation_name} - ${form.industry_sector} (${form.organisation_size})`} onEdit={() => setStep(1)} />
                <SummaryRow label="Home country" value={HOME_COUNTRIES.find(c => c.code === form.home_country)?.name || form.home_country} onEdit={() => setStep(1)} />
                <SummaryRow label="Role" value={`${form.full_name}, ${form.job_title} - ${form.primary_responsibility}`} onEdit={() => setStep(2)} />
                <SummaryRow label="Source countries" value={form.source_countries.length ? form.source_countries.join(', ') : 'Not specified'} onEdit={() => setStep(3)} />
                <SummaryRow label="Import value" value={form.annual_import_value || 'Not specified'} onEdit={() => setStep(3)} />
                <SummaryRow label="Products" value={form.product_categories.join(', ') || 'Not specified'} onEdit={() => setStep(3)} />
                <SummaryRow label="Risk priorities" value={form.risk_priorities.join(', ') || 'Not specified'} onEdit={() => setStep(4)} />
                <SummaryRow label="Goals" value={form.goals.join(', ') || 'Not specified'} onEdit={() => setStep(5)} />
                <SummaryRow label="Alerts" value={form.alert_frequency} onEdit={() => setStep(5)} />
                <SummaryRow label="Reports" value={form.report_cadence} onEdit={() => setStep(5)} />
              </div>
            </StepContainer>
          )}

          {/* Navigation */}
          <div className="mt-auto pt-6 flex items-center justify-between border-t border-[#E8ECF0]">
            <div>
              {step > 1 && (
                <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1.5 text-sm text-[#4E6974] hover:text-[#0A2533] transition-colors" data-testid="onb-back-btn">
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleSkip} className="text-sm text-[#B0BEC5] hover:text-[#4E6974] transition-colors flex items-center gap-1" data-testid="onb-skip-btn">
                <SkipForward className="h-3.5 w-3.5" /> Skip for now
              </button>
              {step < 6 ? (
                <button onClick={() => setStep(s => s + 1)}
                  className="flex items-center gap-1.5 px-5 py-2 bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium rounded-lg transition-colors" data-testid="onb-continue-btn">
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={saving}
                  className="flex items-center gap-1.5 px-5 py-2 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white text-sm font-medium rounded-lg transition-colors" data-testid="onb-confirm-btn">
                  <Check className="h-4 w-4" /> {saving ? 'Saving...' : 'Confirm and go to dashboard'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared components ───

function StepContainer({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-5 w-5 text-[#0097A7]" />
        <h2 className="text-lg font-bold text-[#0A2533]" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>{title}</h2>
      </div>
      <p className="text-sm text-[#4E6974] mb-5">{subtitle}</p>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-[#4E6974] mb-1.5">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#0A2533] focus:border-[#0097A7] outline-none" placeholder={placeholder} />
    </div>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-[#4E6974] mb-1.5">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#0A2533] focus:border-[#0097A7] outline-none bg-white">
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function MultiChip({ items, selected, toggle, testId, small }) {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {items.map(item => {
        const active = selected.includes(item);
        return (
          <button key={item} type="button" onClick={() => toggle(item)}
            className={`${small ? 'px-2 py-0.5 text-xs' : 'px-3 py-1.5 text-sm'} rounded-full border transition-colors ${active ? 'bg-[#00707C] text-white border-[#0097A7]' : 'bg-white text-[#4E6974] border-[#E0E0E0] hover:border-[#0097A7]'}`}
            data-testid={`${testId}-${item.replace(/[\s\/\+]+/g, '-').toLowerCase()}`}>
            {item}
          </button>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value, onEdit }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-[#F5F5F5]">
      <div className="flex-1">
        <span className="text-xs text-[#4E6974] uppercase tracking-wide">{label}</span>
        <p className="text-sm text-[#0A2533] mt-0.5">{value}</p>
      </div>
      <button onClick={onEdit} className="text-[#0097A7] hover:underline text-xs flex items-center gap-1 mt-1 flex-shrink-0">
        <Edit2 className="h-3 w-3" /> Edit
      </button>
    </div>
  );
}
